/**
 * MOTIFS ÉVOLUTIFS — banque de figures PARTAGÉE par les trois automatismes 5e
 * de la rubrique Calcul littéral (programme 2025, cycle 4 page 8) :
 *   1060  identifier des régularités et POURSUIVRE (l'étape suivante) ;
 *   1061  trouver le NOMBRE d'éléments pour une étape donnée ;
 *   1062  identifier la STRUCTURE (« 1 au départ, puis 4 par étape »).
 * Une seule banque, trois angles : « deux ou trois motifs revus toute l'année
 * sous les trois angles valent mieux qu'une collection nouvelle à chaque
 * séance » (cadrage prof 2026-09-10, arbitré par le sous-agent programme-maths
 * qui recommande une banque commune plus large pour un exerciseur, contre le
 * par-cœur et les doublons entre élèves).
 *
 * CONVENTION UNIQUE : valeur(n) = fixe + n × groupe. L'étape 1 vaut donc
 * fixe + groupe (la croix « un point au centre, quatre branches » donne
 * 5 ; 9 ; 13). Les figures à ajouts croissants (carré n², escalier) n'ont pas
 * de structure affine : réservées à 1060, où seule l'étape suivante est
 * demandée (le programme CM2 montre 1, 4, 9, 16 : la régularité n'est pas
 * toujours « + k », c'est le discriminant de « poursuivre »).
 *
 * STRUCTURÉES (la part fixe et les groupes se VOIENT sur le dessin, la
 * longueur d'une branche est le numéro de l'étape) : croix, L, cadre, tours,
 * allumettes en carrés, allumettes en triangles. Elles servent les trois
 * attendus. NON STRUCTURÉES (rangées : un simple alignement, aucune structure
 * lisible) : 1060 et 1061 seulement.
 *
 * Chaque étape du dessin est un groupe SVG nommé `data-etape="1|2|3"` : les
 * solveurs COMPTENT les formes dans l'image et re-dérivent la règle, sans
 * jamais lire les paramètres du générateur.
 *
 * CONTRAINTE PRINT (héritée de 638) : viewBox collé au contenu, libellés à
 * 16 px au moins, l'image s'imprimant souvent en demi-colonne.
 */
import { randomChoice, randInt } from '../../../utils';

// ─── Le domaine ───────────────────────────────────────────────────────────

export const FIGURES = {
    croix: { fixe: 1, groupe: 4, formes: ['points', 'carreaux'], structuree: true },
    etoile: { fixe: 1, groupe: 5, formes: ['points'], structuree: true },
    l: { fixe: 1, groupe: 2, formes: ['points', 'carreaux'], structuree: true },
    t: { fixe: 1, groupe: 3, formes: ['points', 'carreaux'], structuree: true },
    cadre: { fixe: 4, groupe: 4, formes: ['points', 'carreaux'], structuree: true },
    tours: { fixe: 3, groupe: 2, formes: ['carreaux'], structuree: true },
    allumettes_carres: { fixe: 1, groupe: 3, formes: ['allumettes'], structuree: true },
    allumettes_triangles: { fixe: 1, groupe: 2, formes: ['allumettes'], structuree: true },
    rangees: { libre: true, formes: ['points', 'carreaux'], structuree: false },
    carre: { croissant: true, valeur: (n) => n * n, formes: ['carreaux'], structuree: false },
    escalier: { croissant: true, valeur: (n) => (n * (n + 1)) / 2, formes: ['points'], structuree: false },
};

export const STRUCTUREES = ['croix', 'etoile', 'l', 't', 'cadre', 'tours', 'allumettes_carres', 'allumettes_triangles'];
/**
 * INVARIANT : à une étape donnée, deux figures de MÊME élément n'ont jamais le
 * même effectif. Sans cela, le QCM d'images de 1060 aurait deux choix au bon
 * compte et son solveur ne pourrait plus désigner la clé. À l'étape 4 :
 * L 9, escalier 10, T 13, carré 16, croix 17, cadre 20, étoile 21 en points ;
 * L 9, tours 11, T 13, carré 16, croix 17, cadre 20 en carreaux ; allumettes
 * en triangles 9 et en carrés 13.
 */
export const CROISSANTES = ['carre', 'escalier'];
const NOMS = { points: 'points', carreaux: 'carreaux', allumettes: 'allumettes' };
const SINGULIER = { points: 'point', carreaux: 'carreau', allumettes: 'allumette' };

/** « de carreaux », « d'allumettes ». */
export const deNom = (nom) => (/^[aeiouh]/i.test(nom) ? `d'${nom}` : `de ${nom}`);
export const pluriel = (x, nom) => `${x} ${x > 1 ? nom : SINGULIER[nom]}`;

/** Un motif : sa figure, sa forme d'élément, sa règle. */
export const motif = (figure, forme, fixe = null, groupe = null) => {
    const fig = FIGURES[figure];
    const base = { figure, forme, nom: NOMS[forme], structuree: fig.structuree };
    if (fig.croissant) return { ...base, croissant: true, fixe: null, groupe: null, valeur: fig.valeur };
    const f = fig.libre ? fixe : fig.fixe;
    const g = fig.libre ? groupe : fig.groupe;
    return { ...base, croissant: false, fixe: f, groupe: g, valeur: (n) => f + n * g };
};

/**
 * Tire un motif à ajout constant. `sacrifie` est le couple (fixe, groupe)
 * réservé au coup de pouce de l'attendu appelant : il ne sort d'aucune carte.
 * `structureesSeules` réserve le tirage aux figures dont la structure se voit
 * (1062). Une figure sur DEUX a une part fixe autre que 1 : sinon
 * « 1 + N × groupe » se répondait sans regarder (constat prof 2026-09-10).
 */
export const tirerLineaire = ({ sacrifie, structureesSeules = false } = {}) => {
    const dispo = (structureesSeules ? STRUCTUREES : [...STRUCTUREES, 'rangees'])
        .filter((f) => FIGURES[f].libre || !estSacrifie(FIGURES[f].fixe, FIGURES[f].groupe, sacrifie));
    const parPartFixe = [dispo.filter((f) => FIGURES[f].libre || FIGURES[f].fixe === 1),
        dispo.filter((f) => !FIGURES[f].libre && FIGURES[f].fixe !== 1)].filter((l) => l.length);
    for (let essai = 0; essai < 60; essai++) {
        const figure = randomChoice(randomChoice(parPartFixe));
        const fig = FIGURES[figure];
        const forme = randomChoice(fig.formes);
        if (!fig.libre) return motif(figure, forme);
        // rangées : part fixe 1 à 8, groupe 2 à 5 (groupe = 1 exclu, « 1 ; 2 ; 3 » est le numéro de l'étape)
        const fixe = randInt(1, 8);
        const groupe = randInt(2, 5);
        if (estSacrifie(fixe, groupe, sacrifie) || fixe + 3 * groupe > 20) continue;
        return motif(figure, forme, fixe, groupe);
    }
    return motif('l', 'points');
};

export const tirerCroissant = () => {
    const figure = randomChoice(CROISSANTES);
    return motif(figure, FIGURES[figure].formes[0]);
};

const estSacrifie = (fixe, groupe, sacrifie) =>
    !!sacrifie && fixe === sacrifie.fixe && groupe === sacrifie.groupe;

/**
 * MÉMOIRE COURTE, DIX signatures par format (constat prof 2026-09-10 : quand
 * le prof clique « voir une autre variante », il ne veut pas retomber sur la
 * même). Le chemin est bien celui-là : ExerciseurCard appelle generateExercice,
 * qui appelle le generate du gen, donc ceci. La recette du parc, copiée dans
 * 443 endroits, ne retient que la DERNIÈRE signature ; dix couvre une série
 * entière de clics tout en laissant le reste du vivier disponible.
 * Deux limites assumées : la mémoire vit dans la page (deux séances distantes
 * ne se souviennent pas l'une de l'autre), et elle s'arrête à douze essais,
 * pour rendre un doublon plutôt que de boucler si le vivier était plus petit
 * qu'elle.
 */
const MEMOIRE = new Map();
/** Ce qui distingue deux variantes aux yeux de l'élève : l'énoncé, la figure, les choix. */
const signature = (p) => JSON.stringify([
    p.texte,
    p.contenu?.phrase,
    p.contenu?.choix,
    (p.elements_additionnels?.elements || []).map((e) => e.config?.imageUrl || e.config?.lignes),
]);
export const sansRepetition = (cle, produire) => {
    const vues = MEMOIRE.get(cle) || [];
    let candidat = produire();
    for (let essai = 0; essai < 12 && vues.includes(signature(candidat)); essai++) candidat = produire();
    vues.push(signature(candidat));
    MEMOIRE.set(cle, vues.slice(-10));
    return candidat;
};

// ─── Les dessins ──────────────────────────────────────────────────────────

// ─── Les dessins ──────────────────────────────────────────────────────────

const FONT = 'system-ui,sans-serif';
const BLEU = '#2563EB';
const AMBRE = '#F59E0B';
const AMBRE_SOMBRE = '#B45309';
const COL_TEXT = 'currentColor';
const C = 18;

const groupeEtape = (n, corps) => `<g data-etape="${n}">${corps}</g>`;
const etiquette = (x, y, n, anchor = 'middle') =>
    `<text x="${x}" y="${y}" font-size="16" font-family="${FONT}" fill="${COL_TEXT}" text-anchor="${anchor}">Étape ${n}</text>`;
const svgUrl = (W, H, corps) =>
    `data:image/svg+xml,${encodeURIComponent(`<svg viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg">${corps}</svg>`)}`;

/** Une forme dans la case (i, j) d'une grille de pas C, origine (x0, y0) en haut à gauche. */
const forme = (type, x0, y0, i, j) => (type === 'carreaux'
    ? `<rect x="${x0 + i * C + 1}" y="${y0 + j * C + 1}" width="${C - 2}" height="${C - 2}" rx="2" fill="${BLEU}"/>`
    : `<circle cx="${x0 + i * C + C / 2}" cy="${y0 + j * C + C / 2}" r="${C / 2 - 2}" fill="${BLEU}"/>`);

const allumette = (x1, y1, x2, y2) =>
    `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${AMBRE}" stroke-width="4" stroke-linecap="round"/>`
    + `<circle cx="${x2}" cy="${y2}" r="3.5" fill="${AMBRE_SOMBRE}"/>`;

/**
 * UNE étape d'une figure : `UNITE[figure](m)` rend la fonction
 * `(n, x, yBas) => { corps, largeur, hauteur }`, bas aligné sur yBas.
 * Assemblée ensuite en trois étapes côte à côte (l'énoncé) ou seule (un choix
 * de QCM d'images, 1060).
 */
const UNITE = {
    /** Croix : le centre et 4 branches de n. */
    croix: (m) => (n, x, yBas) => {
        const cote = (2 * n + 1) * C;
        const y0 = yBas - cote;
        let corps = forme(m.forme, x, y0, n, n);
        for (let i = 1; i <= n; i++) {
            corps += forme(m.forme, x, y0, n + i, n) + forme(m.forme, x, y0, n - i, n)
                + forme(m.forme, x, y0, n, n + i) + forme(m.forme, x, y0, n, n - i);
        }
        return { corps, largeur: cote, hauteur: cote };
    },
    /** Étoile : le centre et 5 branches de n, en points. */
    etoile: () => (n, x, yBas) => {
        const R = n * C;
        const cx = x + R + C / 2;
        const cy = yBas - R - C / 2;
        let corps = `<circle cx="${cx}" cy="${cy}" r="${C / 2 - 2}" fill="${BLEU}"/>`;
        for (let b = 0; b < 5; b++) {
            const a = -Math.PI / 2 + (b * 2 * Math.PI) / 5;
            for (let i = 1; i <= n; i++) {
                corps += `<circle cx="${(cx + i * C * Math.cos(a)).toFixed(1)}" cy="${(cy + i * C * Math.sin(a)).toFixed(1)}" r="${C / 2 - 2}" fill="${BLEU}"/>`;
            }
        }
        return { corps, largeur: 2 * R + C, hauteur: 2 * R + C };
    },
    /** T : une barre horizontale de 2n + 1 et une tige de n sous son centre. */
    t: (m) => (n, x, yBas) => {
        const large = (2 * n + 1) * C;
        const hauteur = (n + 1) * C;
        const y0 = yBas - hauteur;
        let corps = '';
        for (let i = 0; i <= 2 * n; i++) corps += forme(m.forme, x, y0, i, 0);
        for (let j = 1; j <= n; j++) corps += forme(m.forme, x, y0, n, j);
        return { corps, largeur: large, hauteur };
    },
    /** L : le coin en bas à gauche, une branche vers la droite et une vers le haut. */
    l: (m) => (n, x, yBas) => {
        const cote = (n + 1) * C;
        const y0 = yBas - cote;
        let corps = forme(m.forme, x, y0, 0, n);
        for (let i = 1; i <= n; i++) corps += forme(m.forme, x, y0, i, n) + forme(m.forme, x, y0, 0, n - i);
        return { corps, largeur: cote, hauteur: cote };
    },
    /** Cadre : le bord d'une grille (n + 2) × (n + 2) : 4 coins et 4 côtés de n. */
    cadre: (m) => (n, x, yBas) => {
        const cases = n + 2;
        const cote = cases * C;
        const y0 = yBas - cote;
        let corps = '';
        for (let i = 0; i < cases; i++) {
            for (let j = 0; j < cases; j++) {
                if (i === 0 || j === 0 || i === cases - 1 || j === cases - 1) corps += forme(m.forme, x, y0, i, j);
            }
        }
        return { corps, largeur: cote, hauteur: cote };
    },
    /** Tours : un socle de 3 carreaux, deux colonnes de n posées à ses extrémités. */
    tours: (m) => (n, x, yBas) => {
        const hauteur = (n + 1) * C;
        const y0 = yBas - hauteur;
        let corps = '';
        for (let i = 0; i < 3; i++) corps += forme(m.forme, x, y0, i, n);
        for (let j = 0; j < n; j++) corps += forme(m.forme, x, y0, 0, j) + forme(m.forme, x, y0, 2, j);
        return { corps, largeur: 3 * C, hauteur };
    },
    /** Frise de n carrés en allumettes : 1 au départ, puis 3 par carré (le côté gauche est partagé). */
    allumettes_carres: () => (n, x, yBas) => {
        const s = 40;
        let corps = allumette(x, yBas, x, yBas - s);
        for (let c = 0; c < n; c++) {
            const xc = x + c * s;
            corps += allumette(xc, yBas - s, xc + s, yBas - s)
                + allumette(xc, yBas, xc + s, yBas)
                + allumette(xc + s, yBas, xc + s, yBas - s);
        }
        return { corps, largeur: n * s, hauteur: s };
    },
    /** Frise de n triangles en allumettes (△▽△…) : 1 au départ, puis 2 par triangle. */
    allumettes_triangles: () => (n, x, yBas) => {
        const demi = 24;
        const h = 42;
        let corps = allumette(x, yBas, x + demi, yBas - h);
        for (let t = 0; t < n; t++) {
            const xt = x + t * demi;
            if (t % 2 === 0) {
                corps += allumette(xt, yBas, xt + 2 * demi, yBas) + allumette(xt + 2 * demi, yBas, xt + demi, yBas - h);
            } else {
                corps += allumette(xt, yBas - h, xt + 2 * demi, yBas - h) + allumette(xt + 2 * demi, yBas - h, xt + demi, yBas);
            }
        }
        return { corps, largeur: (n + 1) * demi, hauteur: h };
    },
    /** Carré n × n (ajouts croissants : 1 ; 4 ; 9). */
    carre: (m) => (n, x, yBas) => {
        let corps = '';
        for (let l = 0; l < n; l++) for (let c = 0; c < n; c++) corps += forme(m.forme, x, yBas - n * C, c, l);
        return { corps, largeur: n * C, hauteur: n * C };
    },
    /** Escalier : n rangées, celle du haut en a 1, celle du bas en a n (1 ; 3 ; 6). */
    escalier: (m) => (n, x, yBas) => {
        let corps = '';
        for (let l = 0; l < n; l++) for (let c = 0; c <= l; c++) corps += forme(m.forme, x, yBas - n * C, c, l);
        return { corps, largeur: n * C, hauteur: n * C };
    },
    /** Rangée simple : un alignement, aucune structure à lire. */
    rangees: (m) => (n, x, yBas) => {
        const v = m.valeur(n);
        let corps = '';
        for (let i = 0; i < v; i++) corps += forme(m.forme, x, yBas - C, i, 0);
        return { corps, largeur: v * C, hauteur: C };
    },
};

/** Trois étapes côte à côte, bas alignés, libellé sous chacune. */
const coteACote = (dessin) => {
    const ECART = 36;
    const CASE_MIN = 64; // le libellé « Étape 1 » (16 px) a besoin de cette largeur
    const mesures = [1, 2, 3].map((n) => dessin(n, 0, 0));
    const hauteur = Math.max(...mesures.map((d) => d.hauteur));
    const yBas = 8 + hauteur;
    let x = 8;
    let corps = '';
    for (let n = 1; n <= 3; n++) {
        const large = Math.max(mesures[n - 1].largeur, CASE_MIN);
        const d = dessin(n, x + (large - mesures[n - 1].largeur) / 2, yBas);
        corps += groupeEtape(n, d.corps) + etiquette(x + large / 2, yBas + 24, n);
        x += large + ECART;
    }
    return svgUrl(x - ECART + 8, yBas + 32, corps);
};

/** Trois rangées EMPILÉES, libellé à gauche : côte à côte, un alignement de 17 serait illisible. */
const empilees = (m) => {
    const dessin = UNITE.rangees(m);
    const x0 = 84;
    let corps = '';
    let large = 0;
    for (let n = 1; n <= 3; n++) {
        const yBas = 12 + (n - 1) * 30 + C;
        const d = dessin(n, x0, yBas);
        large = Math.max(large, d.largeur);
        corps += etiquette(8, yBas - 3, n, 'start') + groupeEtape(n, d.corps);
    }
    return svgUrl(x0 + large + 8, 12 + 2 * 30 + C + 12, corps);
};

/** UNE étape seule, sans libellé ni groupe : un choix de QCM d'images. */
const uneEtape = (dessin, n) => {
    const mesure = dessin(n, 0, 0);
    const yBas = 8 + mesure.hauteur;
    return svgUrl(mesure.largeur + 16, yBas + 8, dessin(n, 8, yBas).corps);
};

/** L'énoncé : les trois premières étapes. */
export const imageMotif = (m) => (m.figure === 'rangees' ? empilees(m) : coteACote(UNITE[m.figure](m)));
/** Un choix de QCM d'images : la figure d'une seule étape. */
export const imageEtape = (m, n) => uneEtape(UNITE[m.figure](m), n);

export const elImage = (m) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: imageMotif(m) } }] });
export const paramsMotif = (m, extra = {}) => ({
    figure: m.figure, forme: m.forme, nom: m.nom, structuree: m.structuree,
    croissant: !!m.croissant, fixe: m.fixe, groupe: m.groupe, ...extra,
});
